import {ExclamationCircleOutlined} from '@ant-design/icons';
import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card, message, Modal, Tabs, Tag} from 'antd';
import {
  IdVerificationStatus,
  InterventionType,
  PibisiStatus as PibisiStatusEnum,
  Representative,
  RepresentativeInput,
  useVerifyIdMutation
} from 'api/generated';
import {AnchorLink, PropertyList, Stack} from 'components';
import {PhoneNumberInput} from 'components/Form';
import {FieldArray, Formik} from 'formik';
import {DatePicker, Form, FormItem, Input, InputNumber, Select, SubmitButton} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {useCountries} from 'hooks/useCountries';
import humanize from 'humanize-string';
import {checkSimplifiedAccount} from 'lib/accountUtils';
import {
  formatAddress,
  formatDate,
  formatEmail,
  formatError,
  formatGender,
  formatHumanString,
  formatPhone
} from 'lib/formaters';
import {idDocumentTypeLabels} from 'lib/labels';
import {omitEmpty} from 'lib/omitEmpty';
import {pick} from 'lib/utils';
import {Moment} from 'moment';
import moment from 'moment-timezone';
import React, {useState} from 'react';
import {IdStatus} from 'routes/Account/IdStatus';
import {getIdStatusMessage} from 'routes/Account/utils';
import * as yup from 'yup';
import {PibisiStatus} from './PibisiStatus';
import {SectionProps} from './types';

const {TabPane} = Tabs;
const {Option} = Select;

const filterRepValues = (reps?: Representative[] | null): RepresentativeInput[] => {
  if (!reps) return [];
  return reps.map((rep) => ({
    ...pick(rep, [
      'firstName',
      'lastName',
      'address',
      'email',
      'phone',
      'documentNumber',
      'interventionType',
      'stocksPercentage',
      'nationality',
      'citizenship',
      'status',
      'dateOfBirth'
    ]),
    pibisi: {status: rep.pibisi?.status}
  }));
};

const getFullName = (rep?: Representative) => {
  if (!rep) return 'New';
  const name = [rep.firstName, rep.lastName].filter(Boolean);
  if (name.length === 0) return 'New';
  return name.join(' ');
};

const interventionTypes: Record<InterventionType, string> = {
  TRE: 'Owner (TRE)',
  AUT: 'Authorized (AUT)',
  REP: 'Representative (REP)',
  OTR: 'Other (OTR)',
  TIT: 'Business (TIT)'
};

const schema = yup.object().shape({
  representatives: yup.array().of(
    yup.object().shape({
      firstName: yup.string().nullable().required(),
      lastName: yup.string().nullable().required(),
      email: yup.string().nullable(),
      phone: yup.string().nullable(),
      status: yup.string().oneOf(Object.values(IdVerificationStatus)).required(),
      documentNumber: yup.string().when('status', {
        is: IdVerificationStatus.APPROVED,
        then: yup.string().nullable().required(),
        otherwise: yup.string().nullable()
      }),
      dateOfBirth: yup.string().when('status', {
        is: IdVerificationStatus.APPROVED,
        then: yup.string().nullable().required(),
        otherwise: yup.string().nullable()
      }),
      nationality: yup.string().when('status', {
        is: IdVerificationStatus.APPROVED,
        then: yup.string().nullable().required(),
        otherwise: yup.string().nullable()
      }),
      citizenship: yup.string().when('status', {
        is: IdVerificationStatus.APPROVED,
        then: yup.string().nullable().required(),
        otherwise: yup.string().nullable()
      }),
      interventionType: yup
        .array()
        .of(yup.string().oneOf(Object.values(InterventionType)))
        .test(
          'valid-role-combination',
          'Invalid role combination. Only TRE and REP can be combined, and maximum 2 roles allowed.',
          function (value) {
            if (!value) return true;
            if (value.length > 2) return false;

            // If only one role, it's valid
            if (value.length <= 1) return true;

            // For two roles, only TRE+REP combination is allowed
            const [role1, role2] = value;
            return (
              (role1 === InterventionType.TRE && role2 === InterventionType.REP) ||
              (role1 === InterventionType.REP && role2 === InterventionType.TRE)
            );
          }
        )
    })
  )
});

export const RepresentativeDetails: React.FC<SectionProps> = ({
  cardProps,
  formProps,
  account,
  onSubmit
}) => {
  const {getIsoCountry, getCountryName, isoCountries, loading} = useCountries();
  const [activeIndex, setActiveIndex] = useState(0);
  const {isAdmin} = useAccessLevel();
  const [isEditing, setEditing] = useState(false);
  const [verifyId] = useVerifyIdMutation();

  if (!account) return <Card {...cardProps} title="Business representative" />;

  const selectedCountry = getIsoCountry(account.business?.country);
  const representatives = account.representatives || [];
  const isSimplified = checkSimplifiedAccount(account);

  const handleVerifyId = async (id: number) => {
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Are you sure?',
      content: 'This will send an email to the representative to verify his/her ID document.',
      okText: 'Send',
      onOk: async () => {
        await verifyId({variables: {id, accountId: account.id}});
        message.success('Verification email sent.');
      }
    });
  };

  return (
    <Card
      {...cardProps}
      title="Business representative"
      extra={
        isEditing || !isAdmin ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> Edit data
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik
          validationSchema={schema}
          onSubmit={async (values, {setTouched}) => {
            await onSubmit(omitEmpty(values));
            setEditing(false);
          }}
          initialValues={{
            id: account.id,
            representatives: filterRepValues(account.representatives)
          }}>
          {({values, setFieldValue, errors, submitForm}) => {
            return (
              <FieldArray name="representatives">
                {({push, remove}) => (
                  <Tabs
                    activeKey={String(activeIndex)}
                    onChange={(key) => setActiveIndex(parseInt(key))}
                    size="small"
                    onEdit={(targetKey, action) => {
                      if (action === 'remove') {
                        if (values.representatives.length === 1) return;
                        remove(parseInt(targetKey as string));
                        const i = activeIndex - 1;
                        setActiveIndex(i < 0 ? 0 : i);
                      }
                      if (action === 'add') {
                        push({
                          firstName: null,
                          lastName: null,
                          documentNumber: null,
                          nationality: null,
                          citizenship: null,
                          dateOfBirth: null
                        });
                        setActiveIndex(activeIndex + 1);
                      }
                    }}
                    type="editable-card">
                    {values.representatives.map((rep, index) => {
                      const name = `representatives[${index}]`;
                      return (
                        <TabPane tab={getFullName(rep as Representative)} key={index}>
                          <Form {...formProps}>
                            <FormItem
                              hasFeedback={false}
                              name={`${name}.firstName`}
                              label="First name">
                              <Input
                                name={`${name}.firstName`}
                                placeholder="First and middle name"
                              />
                            </FormItem>
                            <FormItem
                              hasFeedback={false}
                              name={`${name}.lastName`}
                              label="Last name">
                              <Input name={`${name}.lastName`} placeholder="Last name" />
                            </FormItem>
                            <FormItem
                              hasFeedback={false}
                              name={`${name}.documentNumber`}
                              label="Tax ID (NIF)">
                              <Input name={`${name}.documentNumber`} placeholder="1234567889" />
                            </FormItem>
                            <FormItem name={`${name}.interventionType`} label="Business role">
                              <Select
                                mode="multiple"
                                name={`${name}.interventionType`}
                                placeholder="Business role"
                                showSearch>
                                {Object.keys(InterventionType).map((key) => (
                                  <Option key={key} value={key}>
                                    {interventionTypes[key as InterventionType]}
                                  </Option>
                                ))}
                              </Select>
                            </FormItem>
                            <FormItem name={`${name}.nationality`} label="Nationality">
                              <Select
                                name={`${name}.nationality`}
                                placeholder="Nationality"
                                optionFilterProp="children"
                                showSearch>
                                {isoCountries.map((country) => (
                                  <Option key={country.code} value={country.code}>
                                    {getCountryName(country.code)}
                                  </Option>
                                ))}
                              </Select>
                            </FormItem>
                            <FormItem name={`${name}.citizenship`} label="Citizenship">
                              <Select
                                name={`${name}.citizenship`}
                                placeholder="Citizenship"
                                optionFilterProp="children"
                                showSearch>
                                {isoCountries.map((country) => (
                                  <Option key={country.code} value={country.code}>
                                    {getCountryName(country.code)}
                                  </Option>
                                ))}
                              </Select>
                            </FormItem>
                            <FormItem name={`${name}.address.country`} label="Address">
                              <Select
                                name={`${name}.address.country`}
                                placeholder="Country"
                                optionFilterProp="children"
                                showSearch
                                loading={loading}>
                                {isoCountries.map((country) => (
                                  <Option key={country.code} value={country.code}>
                                    {getCountryName(country.code)}
                                  </Option>
                                ))}
                              </Select>
                            </FormItem>
                            <FormItem
                              name={`${name}.address.state`}
                              label="State / Province / Region">
                              {selectedCountry?.divisions ? (
                                <Select
                                  name={`${name}.address.state`}
                                  optionFilterProp="children"
                                  showSearch
                                  placeholder="State / Province / Region">
                                  {Object.keys(selectedCountry.divisions)
                                    .sort((code1, code2) =>
                                      selectedCountry.divisions[code1].localeCompare(
                                        selectedCountry.divisions[code2]
                                      )
                                    )
                                    .map((code) => (
                                      <Option
                                        key={selectedCountry.divisions[code]}
                                        value={selectedCountry.divisions[code]}>
                                        {selectedCountry.divisions[code]}
                                      </Option>
                                    ))}
                                </Select>
                              ) : (
                                <Input
                                  name={`${name}.address.state`}
                                  placeholder="State / Province / Region"
                                />
                              )}
                            </FormItem>
                            <FormItem
                              hasFeedback={false}
                              name={`${name}.address.city`}
                              label="City">
                              <Input name={`${name}.address.city`} placeholder="City" />
                            </FormItem>
                            <FormItem
                              hasFeedback={false}
                              name={`${name}.address.line1`}
                              label="Street, 123">
                              <Input name={`${name}.address.line1`} placeholder="Street, 123" />
                            </FormItem>
                            <FormItem
                              hasFeedback={false}
                              name={`${name}.address.zip`}
                              label="Zip / Postal code">
                              <Input name={`${name}.address.zip`} placeholder="Zip / Postal code" />
                            </FormItem>
                            <FormItem hasFeedback={false} name={`${name}.email`} label="Email">
                              <Input name={`${name}.email`} placeholder="email@example.com" />
                            </FormItem>
                            <FormItem hasFeedback={false} name={`${name}.phone`} label="Phone">
                              <PhoneNumberInput
                                name={`${name}.phone`}
                                countryCode={selectedCountry?.code}
                              />
                            </FormItem>
                            <FormItem name={`${name}.dateOfBirth`} label="Date of birth">
                              <DatePicker
                                allowClear={false}
                                format="L"
                                onChange={(moment) =>
                                  setFieldValue(
                                    `${name}.dateOfBirth`,
                                    (moment as Moment).format('YYYY-MM-DD')
                                  )
                                }
                                showToday={false}
                                name={`${name}.dateOfBirth`}
                                disabledDate={(date) =>
                                  date ? date.isAfter(moment().endOf('day')) : false
                                }
                              />
                            </FormItem>
                            <FormItem
                              name={`${name}.stocksPercentage`}
                              label="Part of the business owned"
                              hasFeedback={false}>
                              <InputNumber
                                name={`${name}.stocksPercentage`}
                                addonAfter="%"
                                placeholder="50"
                              />
                            </FormItem>
                            <FormItem name={`${name}.status`} label="Status">
                              <Select name={`${name}.status`}>
                                {Object.values(IdVerificationStatus).map((status) => (
                                  <Option key={status} value={status}>
                                    {humanize(status)}
                                  </Option>
                                ))}
                              </Select>
                            </FormItem>
                            <FormItem name={`${name}.pibisi.status`} label="PIBISI Status">
                              <Select
                                name={`${name}.pibisi.status`}
                                defaultValue={rep.pibisi?.status}>
                                {Object.values(PibisiStatusEnum).map((status) => (
                                  <Option key={status} value={status}>
                                    {humanize(status)}
                                  </Option>
                                ))}
                              </Select>
                            </FormItem>
                            <FormItem
                              wrapperCol={{
                                offset: formProps.labelCol?.span,
                                ...formProps.wrapperCol
                              }}
                              name="submit">
                              <Stack>
                                <Button
                                  onClick={() => {
                                    setEditing(false);
                                    if (activeIndex >= representatives.length) {
                                      setActiveIndex(representatives.length - 1);
                                    }
                                  }}>
                                  Cancel
                                </Button>
                                <SubmitButton
                                  disabled={false}
                                  onClick={() => {
                                    if (
                                      Array.isArray(errors.representatives) &&
                                      !errors.representatives[activeIndex]
                                    ) {
                                      const index = errors.representatives.findIndex(Boolean);
                                      if (index !== -1) setActiveIndex(index);
                                    }
                                    return submitForm();
                                  }}>
                                  Submit
                                </SubmitButton>
                              </Stack>
                            </FormItem>
                          </Form>
                        </TabPane>
                      );
                    })}
                  </Tabs>
                )}
              </FieldArray>
            );
          }}
        </Formik>
      ) : (
        <Tabs
          activeKey={String(activeIndex)}
          onChange={(key) => setActiveIndex(parseInt(key))}
          size="small"
          type="card">
          {representatives.map((rep, index) => (
            <TabPane tab={getFullName(rep)} key={index}>
              <PropertyList
                omitEmpty
                style={rep.document ? {marginBottom: 15} : {}}
                properties={[
                  {
                    label: 'Name',
                    value: getFullName(rep)
                  },
                  {
                    label: 'Tax Identification Number (NIF)',
                    value: rep.documentNumber,
                    copyable: true
                  },
                  {
                    label: 'Business role',
                    value: rep.interventionType?.map((type) => (
                      <Tag key={type}>{interventionTypes[type as InterventionType]}</Tag>
                    ))
                  },
                  {
                    label: 'Nationality',
                    value: getCountryName(rep.nationality)
                  },
                  {
                    label: 'Citizenship',
                    value: getCountryName(rep.citizenship)
                  },
                  {label: 'Address', value: formatAddress(rep.address)},
                  {label: 'Email', value: formatEmail(rep.email)},
                  {label: 'Phone', value: formatPhone(rep.phone, rep.address?.country, true)},
                  {label: 'Gender', value: formatGender(rep.gender)},
                  {label: 'Date of birth', value: formatDate(rep.dateOfBirth)},
                  {
                    label: 'Part of the business owned',
                    value: rep.stocksPercentage ? `${rep.stocksPercentage}%` : null
                  },
                  {
                    label: 'Status',
                    value: !isSimplified ? <IdStatus status={rep.status} /> : null
                  },
                  {
                    label: 'Status message',
                    value: formatError(getIdStatusMessage(rep.statusCode))
                  },
                  {
                    label: 'PIBISI Status',
                    value: rep.pibisi?.status && (
                      <PibisiStatus status={rep.pibisi.status} accountId={account.id} />
                    )
                  },
                  {
                    label: 'Verification session',
                    value: rep.idSession ? (
                      <AnchorLink
                        href={`https://station.veriff.com/verifications/${rep.idSession}`}>
                        {rep.idSession}
                      </AnchorLink>
                    ) : null
                  }
                ]}
              />
              {rep.document && (
                <>
                  <h4>Document</h4>
                  <PropertyList
                    omitEmpty
                    properties={[
                      {
                        label: 'Type',
                        value: idDocumentTypeLabels[rep.document?.type!]
                      },
                      {label: 'Number', value: rep.document?.number},
                      {label: 'Country', value: getCountryName(rep.document?.country)},
                      {label: 'Valid from', value: formatDate(rep.document?.validFrom)},
                      {label: 'Valid until', value: formatDate(rep.document?.validUntil)}
                    ]}
                  />
                </>
              )}
              {rep.riskLabels?.length ? (
                <>
                  <h4>Risk flags</h4>
                  <PropertyList
                    omitEmpty
                    properties={rep.riskLabels.map((item) => ({
                      label: formatHumanString(item.category)!,
                      value: formatError(item.label)
                    }))}
                  />
                </>
              ) : null}
            </TabPane>
          ))}
        </Tabs>
      )}
    </Card>
  );
};
